import axios, { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../utils/misc";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import { fullAPIURL } from "../utils/requests";
import { getAccessToken } from "../utils/tokens";
import { deduplicateEvents } from "./utils/googleSyncUtils";

const googleSyncAxios = axios.create({
  baseURL: `${fullAPIURL}/v2/calendar-sync/`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

googleSyncAxios.interceptors.request.use(function (config) {
  const token = getAccessToken();
  if (config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

googleSyncAxios.interceptors.request.use(refreshAccessTokenInterceptor);

export interface PostConnectToGoogle {
  clientId: string;
  userId: string;
  gymName: string;
  timezone: string;
  redirectUrl: string;
}

export async function postConnectToGoogle(
  bodyData: PostConnectToGoogle,
): Promise<{
  authorizedUrl: string;
}> {
  const response = await googleSyncAxios.post("authorize", bodyData);
  return response.data;
}

export function disconnectFromGoogle(
  userUuid: string,
  clientUuid: string,
): Promise<AxiosResponse> {
  return googleSyncAxios.delete<void>(
    `authorize/${userUuid}?clientId=${clientUuid}`,
  );
}

export async function getAuthorizationCheck(
  userUuid?: string,
  clientUuid?: string,
): Promise<boolean> {
  if (!userUuid || !clientUuid) return false;

  try {
    const response = await googleSyncAxios.get(
      `authorize/${userUuid}/verify?clientId=${clientUuid}`,
    );
    return response.status === 200 && response.data.valid;
  } catch (err) {
    toast.error("Error getting google connection", defaultToastOptions);
    return false;
  }
}

export interface Calendar {
  name: string;
  id: string;
  timeZone: string;
  description?: string;
  accessRole: string;
  visible: boolean;
  primary?: boolean;
}

export interface GetCalendars {
  primaryCalendar: string;
  calendars: Calendar[];
}

export async function getCalendars(
  userUuid?: string,
  clientUuid?: string,
): Promise<GetCalendars> {
  const response = await googleSyncAxios.get(
    `calendar/${userUuid}?clientId=${clientUuid}`,
  );
  return response.data;
}

export function updateCalendars(
  userUuid: string,
  clientUuid: string,
  calendars: Calendar[],
): Promise<AxiosResponse> {
  return googleSyncAxios.put<GetCalendars>(
    `calendar/${userUuid}/settings?clientId=${clientUuid}`,
    {
      calendars,
    },
  );
}

export async function getGoogleCalendarEvent(userId: string, clientId: string, minDateTime: string, maxDateTime: string): Promise<GoogleCalendarEvent[]> {
  const url = `event/${userId}/search`;
  const response = await googleSyncAxios.get(url, {
    params: {
      clientId,
      minDateTime,
      maxDateTime,
      limit: 100,
    },
  });

  const filteredEvents = deduplicateEvents(response.data.events).filter(event =>
    event.start?.dateTime &&
    new Date(event.start.dateTime) <= new Date(maxDateTime)
  );
  return filteredEvents;
}

type EventDateTime = {
  dateTime?: string;
  date?: string;
  timeZone?: string;
};

type EventAttendee = {
  email: string;
  self?: boolean;
  organizer?: boolean;
  responseStatus: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  optional?: boolean;
  comment?: string;
};

type EventCreatorOrganizer = {
  email: string;
  self?: boolean;
};

type ConferenceDataEntryPoint = {
  entryPointType: 'video' | 'phone' | 'sip' | 'more';
  uri: string;
  label: string;
  meetingCode?: string;
  passcode?: string;
  regionCode?: string;
};

type ConferenceData = {
  entryPoints: ConferenceDataEntryPoint[];
  conferenceSolution: {
    key: {
      type: string;
    };
    name: string;
    iconUri: string;
  };
  conferenceId: string;
  notes: string;
  parameters: {
    addOnParameters: {
      parameters: {
        scriptId: string;
        realMeetingId: string;
        creatorUserId: string;
        meetingUuid: string;
        meetingType: string;
        originalEventId: string;
      };
    };
  };
};

type ExtendedProperties = {
  private?: {
    [key: string]: string;
  };
  shared?: {
    [key: string]: string;
  };
};

export type GoogleCalendarEvent = {
  sequence: boolean;
  name: string;
  kind: string;
  etag: string;
  id: string;
  status: string;
  htmlLink: string;
  created: string;
  updated: string;
  creator: EventCreatorOrganizer;
  organizer: EventCreatorOrganizer;
  start: EventDateTime;
  end: EventDateTime;
  recurrence?: string[];
  iCalUID: string;
  attendees?: EventAttendee[];
  extendedProperties?: ExtendedProperties;
  conferenceData?: ConferenceData;
  reminders: {
    useDefault: boolean;
  };
  eventType: string;
  recurringEventId?: string;
  originalStartTime?: EventDateTime;
  transparency?: string;
  visibility?: string;
  description?: string;
  workingLocationProperties?: {
    type: string;
    homeOffice: Record<string, unknown>;
  };
  guestsCanModify?: boolean;
};


